import React, { useCallback } from "react"
import { Navigate, type RouteObject } from "react-router-dom"

import { PermissionGate } from "../../../../components/PermissionGate/PermissionGate.tsx"
import { ROLE_IDS } from "../../../../constants/roles.ts"
import { RoutesPaths } from "../../../../constants/routes.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import {
  selectActiveBranch,
  selectActiveBranchId,
  selectCompanyIsMultiBranch,
  selectIsOwner
} from "../../../Branches/redux/branches.slice.ts"
import OpenInCompanyHQGate from "../../../Branches/components/OpenInCompanyHQGate.tsx"
import DocumentTemplatesPage from "../../../DocumentTemplates/DocumentTemplatesPage/DocumentTemplatesPage.tsx"
import { EmailTemplatesPage } from "../../../EmailTemplates/EmailTemplatesPage.tsx"
import { SettingsLayout } from "../../../Settings/components/common/SettingsLayout.tsx"
import SettingNotificationPage from "../../../Settings/pages/SettingNotificationPage.tsx"
import SettingsCompanyPage from "../../../Settings/pages/SettingsCompanyPage.tsx"
import SettingsBranchDetailPage from "../../../Branches/pages/SettingsBranchDetailPage.tsx"
import SettingsBranchesPage from "../../../Branches/pages/SettingsBranchesPage.tsx"
import SettingsConfigurationPage from "../../../Settings/pages/SettingsConfigurationPage.tsx"
import SettingsFormFieldRequirementsPage from "../../../Settings/pages/SettingsFormFieldRequirementsPage.tsx"
import SettingsLeadSourceBudgetPage from "../../../Settings/pages/SettingsLeadSourceBudgetPage.tsx"
import SettingsListsPage from "../../../Settings/pages/SettingsListsPage.tsx"
import SettingsProfilePage from "../../../Settings/pages/SettingsProfilePage.tsx"
import SettingsSmsUsagePage from "../../../Settings/pages/SettingsSmsUsagePage.tsx"
import SettingsTemplatesPage from "../../../Settings/pages/SettingsTemplatesPage.tsx"
import AutomationBuilderPage from "../../../SettingsAutomation/AutomationBuilderPage.tsx"
import AutomationOverviewPage from "../../../SettingsAutomation/AutomationOverviewPage.tsx"
import DashboardTemplatesSettings from "../../../SettingsDashboardTemplates/DashboardTemplatesSettings.tsx"
import NewPermissionsSettings from "../../../SettingsPermissions/PermissionsSettings.tsx"
import ProjectTypePage from "../../../Templates/ProjectTypePage"
import ProjectTypesTemplatesPage from "../../../Templates/ProjectTypesTemplatesPage"
import { LegacyProjectTemplateRedirect } from "./LegacyTemplateRedirects.tsx"

// Owners and administrators of a company with the feature on; the backend
// refuses everyone else, so the route does too rather than render a 403.
function LeadSourceBudgetGate({ children }: { children: React.ReactNode }) {
  const allowed = useAppSelector(
    (state) => !!state.auth.userInfo?.feature_marketing_source_performance
  )
  const check = useCallback(() => allowed, [allowed])
  return <PermissionGate check={check}>{children}</PermissionGate>
}

// The tab is hidden without the company's lead-automation flag, so every
// Automation route must refuse a direct visit too, not just the permission-less.
function AutomationGate({ children }: { children: React.ReactNode }) {
  const flagOn = useAppSelector(
    (state) => !!state.auth.userInfo?.feature_lead_automation
  )
  const check = useCallback(() => flagOn, [flagOn])
  return (
    <PermissionGate
      paths={["settings.automations.settings_automation_view"]}
      check={check}
    >
      {children}
    </PermissionGate>
  )
}

// `role?.id === ADMIN_GCC` alone misses a real owner: `invite_gcc_admin`
// (Django Admin's "Invite GCC Owner" action) deliberately leaves the
// legacy `User.role` at its TEAM_MEMBER default ("TODO refactor as we
// wont have gccadmin role in future", `apps/user/admin.py`) and instead
// grants ownership through `GccAssignment.is_owner` + the "gcc" permission
// label — the same signal `branches-accessible`'s `is_owner` (and this
// selector) already reads. Without this, a Django-provisioned multi-branch
// owner's first visit to Settings → Branches reads "Access Restricted".
//
// `isMultiBranch` (the real `Gcc.multi_branch_enabled` flag, never
// role/branch-count-inferred) is required in the SAME check, not just on
// the nav tab: the tab was already hidden for a single-operation owner,
// but nothing stopped them reaching this route directly by URL/bookmark/
// back-button and seeing "Add branch" and branch terminology they must
// never see at all.
function BranchesOwnerGate({ children }: { children: React.ReactNode }) {
  const isOwner = useAppSelector(selectIsOwner)
  const isMultiBranch = useAppSelector(selectCompanyIsMultiBranch)
  const check = useCallback(
    (_permissions: unknown, role: { id?: number } | undefined) =>
      isMultiBranch &&
      (isOwner ||
        role?.id === ROLE_IDS.ADMIN_GCC ||
        role?.id === ROLE_IDS.ADMIN_PLATFORM),
    [isOwner, isMultiBranch]
  )
  return (
    <PermissionGate
      description="Only the company owner can manage branches."
      check={check}
    >
      {children}
    </PermissionGate>
  )
}

// Same bug as BranchesOwnerGate above, same fix: this route's own `check`
// only ever tested `role?.id`, missing the same Django-provisioned/
// GccAssignment-owner case — a real owner whose legacy `role.id` is not
// literally ADMIN_GCC saw a silently blank Company Defaults tab (the tab
// itself is visible, driven by `SettingsLayoutTabs`' own, already-correct
// `canManageConfiguration = ADMIN_GCC || ADMIN_PLATFORM || isOwner`; only
// this route's gate had drifted from it). `hideOnDenied` is kept: a
// template-permission-only holder still needs the tab reachable to open
// the Templates subtab, even though this particular subtab blanks for them.
//
// A second, later regression: a Branch Manager viewing their OWN branch
// workspace could reach the "Company Defaults" tab (via one of its other
// permission-code `isVisible` branches — the role mirrors `gcc_admin`'s
// near-everything permission set) only to find THIS gate still refusing
// them, since it never recognized `isBranchWorkspace && isActiveBranchManager`
// at all — the exact branch `SettingsLayoutTabs`'/`SettingsCompanyPage`'s
// own checks already use. Deliberately NOT mirroring this gate's other
// permission-code branches (`settings_template_view` etc.) here: those
// codes are global, not scoped to the active branch, so admitting them
// would let a Branch Manager reach Company HQ's own ID-configuration/
// Automatic-Project-Creation settings while viewing HQ — exactly the
// "cannot access HQ config" boundary this fix must not cross. The page
// itself (`SettingsConfigurationPage`) already renders the branch's own
// `BranchConfigurationTab` instead of the HQ-wide content whenever
// `isBranchWorkspace` is true, so admitting the branch manager only in
// that mode is safe.
function ConfigurationManagerGate({ children }: { children: React.ReactNode }) {
  const isOwner = useAppSelector(selectIsOwner)
  const isMultiBranch = useAppSelector(selectCompanyIsMultiBranch)
  const activeBranchId = useAppSelector(selectActiveBranchId)
  const activeBranch = useAppSelector(selectActiveBranch)
  const isBranchWorkspace = isMultiBranch && !!activeBranchId
  const isActiveBranchManager = !!activeBranch?.is_manager
  const check = useCallback(
    (_permissions: unknown, role: { id?: number } | undefined) =>
      isOwner ||
      role?.id === ROLE_IDS.ADMIN_GCC ||
      role?.id === ROLE_IDS.ADMIN_PLATFORM ||
      (isBranchWorkspace && isActiveBranchManager),
    [isOwner, isBranchWorkspace, isActiveBranchManager]
  )
  return (
    <PermissionGate hideOnDenied check={check}>
      {children}
    </PermissionGate>
  )
}

export const settingsRoutes: RouteObject[] = [
  {
    path: RoutesPaths.settings.profile,
    element: <SettingsLayout />,
    children: [
      { index: true, element: <SettingsProfilePage /> },
      {
        path: RoutesPaths.settings.company,
        element: (
          <PermissionGate paths={["settings.company.settings_company_view"]}>
            <SettingsCompanyPage />
          </PermissionGate>
        )
      },
      {
        path: RoutesPaths.settings.templates,
        element: (
          <PermissionGate
            mode={"some"}
            paths={[
              "settings.project_templates.settings_template_view",
              "settings.document_templates.settings_proposal_template_view",
              "settings.permissions.settings_permissions_view",
              "settings.email_templates.settings_email_template_view"
            ]}
          >
            <SettingsTemplatesPage />
          </PermissionGate>
        )
      },
      {
        path: RoutesPaths.settings.projectTemplates,
        element: (
          <PermissionGate
            paths={["settings.project_templates.settings_template_view"]}
          >
            <ProjectTypesTemplatesPage />
          </PermissionGate>
        )
      },
      {
        path: RoutesPaths.settings.projectTemplate(":id"),
        element: (
          <PermissionGate
            paths={["settings.project_templates.settings_template_edit"]}
          >
            <ProjectTypePage />
          </PermissionGate>
        )
      },
      {
        path: RoutesPaths.settings.documentTemplates,
        element: (
          <PermissionGate
            paths={[
              "settings.document_templates.settings_proposal_template_view"
            ]}
          >
            <DocumentTemplatesPage />
          </PermissionGate>
        )
      },
      {
        path: RoutesPaths.settings.emailTemplates,
        element: (
          <PermissionGate
            paths={["settings.email_templates.settings_email_template_view"]}
          >
            <EmailTemplatesPage />
          </PermissionGate>
        )
      },
      {
        path: RoutesPaths.settings.dashboardTemplates,
        element: (
          <PermissionGate
            paths={["settings.permissions.settings_permissions_view"]}
          >
            <DashboardTemplatesSettings />
          </PermissionGate>
        )
      },
      {
        path: RoutesPaths.settings.lists,
        element: (
          <PermissionGate
            paths={["settings.project_templates.settings_template_view"]}
          >
            <SettingsListsPage />
          </PermissionGate>
        )
      },
      {
        path: RoutesPaths.settings.leadSourceBudget,
        element: (
          <LeadSourceBudgetGate>
            <SettingsLeadSourceBudgetPage />
          </LeadSourceBudgetGate>
        )
      },
      {
        path: RoutesPaths.settings.formFieldRequirements,
        element: (
          <PermissionGate
            paths={["settings.form_requirements.form_requirements_manage"]}
          >
            <SettingsFormFieldRequirementsPage />
          </PermissionGate>
        )
      },
      {
        path: "/settings/document_templates",
        element: (
          <Navigate replace to={RoutesPaths.settings.documentTemplates} />
        )
      },
      {
        path: "/settings/dashboard_templates",
        element: (
          <Navigate replace to={RoutesPaths.settings.dashboardTemplates} />
        )
      },
      {
        path: "/settings/templates/:id",
        element: <LegacyProjectTemplateRedirect />
      },
      {
        path: RoutesPaths.settings.permissions,
        element: (
          <PermissionGate
            paths={["settings.permissions.settings_permissions_view"]}
          >
            <NewPermissionsSettings />
          </PermissionGate>
        )
      },
      {
        path: RoutesPaths.settings.notifications,
        element: <SettingNotificationPage />
      },
      {
        path: RoutesPaths.settings.configuration,
        element: (
          <ConfigurationManagerGate>
            <SettingsConfigurationPage />
          </ConfigurationManagerGate>
        )
      },
      {
        // No hideOnDenied here, deliberately unlike Configurations: a
        // branch-restricted manager typing this URL directly must see the
        // app's real "not allowed" experience, not a blank tab-less page.
        path: RoutesPaths.settings.branches,
        element: (
          <BranchesOwnerGate>
            <OpenInCompanyHQGate>
              <SettingsBranchesPage />
            </OpenInCompanyHQGate>
          </BranchesOwnerGate>
        )
      },
      {
        path: RoutesPaths.settings.branch(":id"),
        element: (
          <BranchesOwnerGate>
            <OpenInCompanyHQGate>
              <SettingsBranchDetailPage />
            </OpenInCompanyHQGate>
          </BranchesOwnerGate>
        )
      },
      {
        path: RoutesPaths.settings.smsUsage,
        element: (
          <PermissionGate
            hideOnDenied
            check={(permissions, role) =>
              role?.id === ROLE_IDS.ADMIN_PLATFORM ||
              !!permissions?.codes?.settings?.company?.settings_company_view
            }
          >
            <SettingsSmsUsagePage />
          </PermissionGate>
        )
      },
      {
        path: RoutesPaths.settings.automation,
        element: (
          <AutomationGate>
            <AutomationOverviewPage />
          </AutomationGate>
        )
      },
      {
        path: RoutesPaths.settings.automationNew,
        element: (
          <AutomationGate>
            <AutomationBuilderPage />
          </AutomationGate>
        )
      },
      {
        path: RoutesPaths.settings.automationEdit(":id"),
        element: (
          <AutomationGate>
            <AutomationBuilderPage />
          </AutomationGate>
        )
      }
    ]
  }
]
